import { useBikesStore } from '@bikes/store/store';
import { ConfirmationTemplate } from '@templates';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { RoutesList } from '../../../routes/Routes';
import { basename } from '../../../routes/basename';
import { FormTitle } from '@molecules/*';
import {
  CompaniesEmails,
  HireBikeCompanies,
  displayText,
} from '@bikes/constants/hireCompanies';
import { Link } from '@atoms/*';
import { BikesType } from '@bikes/store/storeSlices/bikesDetails';

export const Confirmation = () => {
  const {
    bikesType,
    hireCompany,
    caseReferenceId,
    slaText,
    receiveConfirmationEmail,
    resetBikesDetails,
    resetYourDetails,
    email,
  } = useBikesStore();

  const location = useLocation();

  const [tempEmail, setTempEmail] = useState('');

  useEffect(() => {
    if (tempEmail === '') {
      setTempEmail(email);
    }
    // push 10 states to history to prevent user from going back to previous pages
    for (let i = 0; i < 10; i++) {
      window.history.pushState(
        { page: i },
        '',
        basename + RoutesList.CONFIRMATION,
      );
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      // Prevent default behavior and navigate to home page
      window.location.replace('https://www.westminster.gov.uk/report-it');
    };

    window.addEventListener('popstate', handlePopState);
    resetBikesDetails();
    resetYourDetails();

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const getHireCompanyEmail = () => {
    if (hireCompany) {
      if (hireCompany.includes('Forest')) {
        return CompaniesEmails?.[HireBikeCompanies.Forest];
      } else {
        // @ts-ignore
        return CompaniesEmails?.[hireCompany];
      }
    }
    return '';
  };

  const confirmationTitle =
    bikesType === BikesType.Bikes
      ? 'Your report has been submitted'
      : `Your report has been sent to ${displayText(hireCompany)}`;
  const contentTitle = `What happens next?`;

  const fixedPenaltyNoticeText = (
    <div className="text-bodyXl text-text-body mb-5 flex flex-col gap-6">
      <p>
        Our City Inspectors also receive this report and may decide to
        investigate further, particularly with persistent or repeated issues.
      </p>
      <p>
        Due to the number of calls and complaints we receive we use an
        intelligence-led approach to this, so not all individual reports will be
        followed up. We will also take enforcement action proactively based on
        the reports we receive.
      </p>
      <p>
        Where City Inspectors attend and find the matter has not been resolved
        satisfactorily, they have the discretion to issue a Fixed Penalty Notice
        to the operating company.
      </p>
    </div>
  );
  const contentSubtext = (
    <div className="text-bodyXl text-text-body mb-5 flex flex-col gap-6">
      <p>Your report has been sent to {displayText(hireCompany)}.</p>
      {receiveConfirmationEmail === 'Yes' ? (
        <p>We have sent a confirmation email to {tempEmail}</p>
      ) : null}
      <p>
        {displayText(hireCompany)} will aim to resolve this issue{' '}
        <strong>within {slaText}.</strong>
      </p>
      {fixedPenaltyNoticeText}
    </div>
  );

  const contentSubtextBikes = (
    <div className="text-bodyXl text-text-body mb-5 flex flex-col gap-6">
      {receiveConfirmationEmail === 'Yes' ? (
        <p>We have sent a confirmation email to {tempEmail}</p>
      ) : null}
      <ul className="ml-6 list-disc">
        <li>
          We will review your report to check if the bike looks abandoned.
        </li>
        <li>
          An inspector will be sent to check the bike in the{' '}
          <strong>next 2 days</strong>. They will apply a warning sticker if
          needed.
        </li>
        <li>
          The warning sticker gives owners <strong>2 days</strong> to claim
          their bike.
        </li>
        <li>
          If the bike is not claimed, we will request a bike removal to one of
          our teams.
        </li>
      </ul>
    </div>
  );

  const contactTitle = `Who to contact`;
  const getContactEmail = getHireCompanyEmail();

  const contactSubtext = (
    <div className="text-bodyXl text-text-body flex flex-col gap-6">
      <p>
        To get an update on this report, you should email{' '}
        {displayText(hireCompany)} (
        <Link
          href={`mailto:${getContactEmail}`}
          className="inline font-normal"
          linkType="inline"
          title="Email to hire company"
          external={true}
        >
          {getContactEmail}
        </Link>
        ). You may be asked to provide the CAS reference number.
      </p>
    </div>
  );

  const navigationButton = {
    label: 'Return to homepage',
    isExternalLink: true,
    href: 'https://www.westminster.gov.uk/report-it ',
  };

  const content =
    bikesType === BikesType.Bikes ? (
      <FormTitle text={contentTitle} subtext={contentSubtextBikes} />
    ) : (
      <>
        <FormTitle text={contentTitle} subtext={contentSubtext} />
        <FormTitle text={contactTitle} subtext={contactSubtext} />
      </>
    );

  return (
    <ConfirmationTemplate
      reference={caseReferenceId}
      content={content}
      button={navigationButton}
      confirmationTitle={confirmationTitle}
      feedbackLink={true}
    />
  );
};
